/**
 * Animation primitives — pure JS, no deps.
 *
 * Three hooks:
 *   useReveal()    — toggles an `is-revealed` flag once the element enters
 *                    viewport (IntersectionObserver, threshold 15%).
 *   useCountUp()   — ease-out cubic count-up that fires when the element
 *                    enters viewport (threshold 40%). Runs once.
 *   useMagnetic()  — translates the element toward the cursor inside its
 *                    hover bounding box. Subtle by default (strength 0.25).
 *
 * All hooks respect `prefers-reduced-motion: reduce`.
 */
import { useEffect, useRef, useState } from "react";

const isReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ------------------------------ useReveal ----------------------------- */
export function useReveal({ threshold = 0.15, once = true, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    if (typeof IntersectionObserver === "undefined" || isReducedMotion()) {
      setRevealed(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setRevealed(false);
        }
      },
      { threshold, rootMargin }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold, once, rootMargin]);

  return [ref, revealed];
}

/* ------------------------------ useCountUp ---------------------------- */
export function useCountUp({ end, duration = 1500, decimals = 0, threshold = 0.4 } = {}) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (isReducedMotion()) {
      setValue(end);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const t0 = performance.now();
          const tick = (now) => {
            const t = Math.min((now - t0) / duration, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            setValue(end * eased);
            if (t < 1) requestAnimationFrame(tick);
            else setValue(end);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [end, duration, threshold]);

  const formatted =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
  const separated = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return [ref, separated];
}

/* ------------------------------ useMagnetic --------------------------- */
export function useMagnetic({ strength = 0.25, ease = "transform 250ms cubic-bezier(0.2, 0.7, 0.3, 1)" } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (isReducedMotion()) return undefined;

    el.style.transition = ease;
    el.style.willChange = "transform";

    let raf = 0;
    let tx = 0;
    let ty = 0;

    const apply = () => {
      el.style.transform = `translate3d(${tx.toFixed(2)}px, ${ty.toFixed(2)}px, 0)`;
    };

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const dx = e.clientX - (rect.left + rect.width / 2);
      const dy = e.clientY - (rect.top + rect.height / 2);
      tx = dx * strength;
      ty = dy * strength;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    const onLeave = () => {
      tx = 0;
      ty = 0;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
      el.style.transition = "";
      el.style.transform = "";
      el.style.willChange = "";
    };
  }, [strength, ease]);

  return ref;
}
